import React from 'react';
import { fromPairs, map, find, isEmpty, isEqual, get, merge } from 'lodash';
import classnames from 'classnames';

// local dependencies
import { withNetworkState } from 'ui/hoc/withNetworkState';
import { Form } from 'modules/form';
import { button as Button } from 'modules/button';
import { getLabel } from 'utils/getLabel';
import { inputField as InputField } from 'ui/views/generic/inputField';
import { widgetPreloader as WidgetPreloader } from 'ui/views/generic/widgetPreloader';

/**
 * @type {object}
 * @desc A React presentation/dumb view component.
 * @example
 * import { authFlowCallerIdentificationThirdParty as AuthFlowCallerIdentificationThirdParty } from 'ui/views/widgets/authFlowCallerIdentificationThirdParty';
 *
 * // render
 * <AuthFlowCallerIdentificationThirdParty propName='propValue'/>
 */

// caller detail section ( editable )
const CallerDetailSectionEditable = ( props ) => {
	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const cancelAction = find( callerDetails.actions, { name: 'cancel' } );
	const saveAction = find( callerDetails.actions, { name: 'save' } );

	return (
		<div className='ui-auth-flow-caller-identification-third-party__caller-details'>
			<div className='ui-auth-flow-caller-identification-third-party__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>

			<Form
				fields={ fromPairs( map( fieldsData, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` prop
				values={ props.thirdPartyInfo } // default values
				isValid={ false }
				mode={ props.mode }
				onSubmit={ ( { values, isValid } ) => {

					if( isValid ) {
						props.onFormSubmit( {
							title: values.title,
							firstName: values.firstName,
							lastName: values.lastName
						} );
					}
					
				} }
			>
				{ ( { fields, values, isValid, errors, onChange, onSumbit } = {} ) => {
					const classNameField = classnames( {
						'ui-auth-flow-caller-identification-third-party__caller-details__detailsForm__fields-inputEditable': !isEqual(
							props.mode,
							'readonly'
						),
						'ui-auth-flow-caller-identification-third-party__caller-details__detailsForm__fields-inputReadonly': isEqual(
							props.mode,
							'readonly'
						),
					} );

					return (
						<form
							onSubmit={ onSumbit }
							className='ui-auth-flow-caller-identification-third-party__caller-details__detailsForm'
						>
							{ /* fields */ }
							{ map( fields, ( field ) => {
								return (
									<field.view
										key={ field.name }
										className={ classNameField }
										name={ field.name }
										required={ field.required }
										placeholder={ field.placeholder }
										label={ field.title }
										onChange={ onChange }
										value={ values[ field.name ] }
										error={ errors[ field.name ] }
										mode={ props.mode }
									/>
								);
							} ) }

							{ /* submit/edit action */ }
							<Button
								id={ saveAction.name }
								disabled={ !isValid }
								type='submit'
								size='large'
								label={ saveAction.title }
								className='ui-auth-flow-caller-identification-third-party__caller-details__detailsForm__fields-button'
							/>

							{ /* cancel action */ }
							{ isEmpty( props.thirdPartyInfo ) ||
							isEqual( props.mode, 'readonly' ) ? null : (
								<Button
									id={ cancelAction.name }
									size='large'
									outline={ true }
									title={ cancelAction.title }
									className='ui-auth-flow-caller-identification-third-party__caller-details__detailsForm__fields-button'
									onClick={ () => props.onToggleEditMode( false ) }
								/>
							) }
						</form>
					);
				} }
			</Form>
		</div>
	);
};

// set display name
CallerDetailSectionEditable.displayName =
	'authFlowCallerIdentificationThirdPartySuccess.CallerDetailSectionEditable';

/* ------------------ */

// caller detail section ( readonly )
const CallerDetailSectionReadOnly = ( props ) => {
	const callerDetails = props.componentData.sections.callerDetails;
	const fieldsData = callerDetails.fields;

	// extracting actions
	const editAction = find( callerDetails.actions, { name: 'edit' } );

	return (
		<div className='ui-auth-flow-caller-identification-third-party__caller-details'>
			<div className='ui-auth-flow-caller-identification-third-party__caller-details__title'>
				{ getLabel( callerDetails.title ) }
			</div>

			<div className='ui-auth-flow-caller-identification-third-party__caller-details__readonly-view'>
				{ /* fields */ }
				{ map( fieldsData, ( field ) => {
					return (
						<InputField
							key={ field.name }
							className='ui-auth-flow-caller-identification-third-party__caller-details__readonly-view__field'
							name={ field.name }
							label={ field.title }
							value={ props.thirdPartyInfo[ field.name ] }
							mode='readonly'
						/>
					);
				} ) }

				{ /* edit action */ }
				<Button
					size='large'
					title={ editAction.title }
					onClick={ () => {
						props.onToggleEditMode( true );
					} }
				/>
			</div>
		</div>
	);
 };

// set display name
CallerDetailSectionReadOnly.displayName = 'authFlowCallerIdentificationThirdPartySuccess.CallerDetailSectionReadOnly';

/* ------------------ */

const OrganizationDetails = ( props ) => {
    

    // extract actions for next button
	const actions = get( props.componentData, 'actions' );
    const action = find( actions, { name: 'nextInbound' } );

    // extracting fields
    const orgDetails = props.componentData.sections.organizationDetails;
	const fieldsData = orgDetails.fields;
    
    return (
        <div className='ui-auth-flow-caller-identification-third-party__organization'>
            <Form
                fields={ fromPairs( map( fieldsData, ( field ) => [ field.name, field ] ) ) } // convert to object as specified by `Form` props
                isValid={ false }
                onSubmit={ ( { values, isValid } ) => {
                    
                    if( isValid ) {
                        props.handleActionClick( {
                            item: merge( props.thirdPartyInfo, values ),
                            value: props.actionsForNext,
                        } );
                    }
                } }
            >
                { ( { fields, values, errors, onChange, onSumbit } = {} ) => {
                    return (
                        <form
							onSubmit={ onSumbit }
							className='ui-auth-flow-caller-identification-third-party__organization__detail'
                        >
							<div className='ui-auth-flow-caller-identification-third-party__organization__detail__fields'>
								{ /* fields */ }
								{ map( fields, ( field ) => {
									return (
										<field.view
											key={ field.name }
											className='ui-auth-flow-caller-identification-third-party__organization__detail__fields__field'
											name={ field.name }
											required={ field.required }
											placeholder={ field.placeholder }
											label={ field.title }
											onChange={ onChange }
											value={ values[ field.name ] }
											error={ errors[ field.name ] }
											mask={ field.mask }
										/>
									);
								} ) }
							</div>

                            { /* the next button */ }
                            <Button
								className='ui-auth-flow-caller-identification-third-party__organization__detail__button'
								id={ action.name }
								size='large'
								type='submit'
                                title={ action.title }
                                postIconId='arrow'
                                showPostIcon={ true}
                                iconWidth='20'
								iconHeight='30'
								disabled = { !( !isEmpty( values.organizationName ) && !isEmpty( values.callbackNumber ) && !isEmpty( values.taxId ) ) }
                            />
                        </form>
                    );
                } }
            </Form>
        </div>
    );
};

export const authFlowCallerIdentificationThirdPartySuccess = ( props ) => {

    // dynamic class name
	const widgetClassName = classnames(
		'ui-auth-flow-caller-identification-third-party',
		props.className
	);
    return (
        <div className={ widgetClassName }>
            { /* Caller Details form */ }
			{ isEqual( props.mode, 'readonly' ) ? (
				<CallerDetailSectionReadOnly { ...props } />
			) : (
				<CallerDetailSectionEditable { ...props } />
			) }
            { props.isOrgDetailVisible ? (
                <OrganizationDetails { ...props } />
            ) : (
                null
            ) }
        </div>
    );
};

// set display name
authFlowCallerIdentificationThirdPartySuccess.displayName = 'authFlowCallerIdentificationThirdPartySuccess';

// set default props
authFlowCallerIdentificationThirdPartySuccess.defaultProps = {};

// prop types
authFlowCallerIdentificationThirdPartySuccess.propTypes = {};

/* ------------------ */

export const authFlowCallerIdentificationThirdPartyPreloader = () => {
	return (
		<div className='ui-auth-flow-caller-identification-third-party-preloader'>
			<WidgetPreloader />
		</div>
	);
 };

// set display name
authFlowCallerIdentificationThirdPartyPreloader.displayName =
	'authFlowCallerIdentificationThirdPartyPreloader';

// set default props
authFlowCallerIdentificationThirdPartyPreloader.defaultProps = {};

// prop types
authFlowCallerIdentificationThirdPartyPreloader.propTypes = {};

/* ********************************************************* */

export const authFlowCallerIdentificationThirdParty = withNetworkState(
	authFlowCallerIdentificationThirdPartyPreloader,
	authFlowCallerIdentificationThirdPartySuccess
);

